import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { TabelaAcao, ColunaTabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import PoliticaParceiroDialog from '@/components/produtos/PoliticaParceiroDialog'
import { authStore } from '@/store/authStore'
import { parceirosService } from '@/services/parceirosService'
import { produtoService } from '@/services/produtoService'
import type { Parceiro, ParceiroRequest, ParceiroResumo } from '@/types/parceiro'
import { TIPOS_PARCEIRO } from '@/types/parceiro'
import type { Produto } from '@/types/produto'
import { tokens } from '@/theme'
import ParceiroFormDialog from './ParceiroFormDialog'

const temPapel = (permissoes: string[] | undefined, ...papeis: string[]) =>
  !!permissoes?.some((p) => papeis.includes(p.toUpperCase()))

/**
 * Parceiros (Figma, Onda 7.1 / A12) — cadastro dos parceiros financeiros pelo back-office.
 *
 * Leitura para OPERADOR+ (o consultor vê parceiros só dentro do produto/oportunidade);
 * escrita (novo, editar, ativar/desativar) para GERENTE e ADMIN — o mesmo corte do backend.
 *
 * KPIs citam a MESMA fonte da tabela (`/v1/parceiros/resumo` conta a tabela de parceiros e a
 * de políticas). "Seleções" não tem projeção em produtos (a escolha do parceiro vive na
 * reserva, em oportunidades) — mostra "—", nunca um número inventado. "Matching automático"
 * é texto de roteiro: hoje a compatibilidade é conferida por operação, na oportunidade.
 */
export const ParceirosPage: FC = () => {
  const usuario = authStore((s) => s.usuario)
  const podeVer = useMemo(
    () => temPapel(usuario?.permissoes, 'ADMIN', 'GERENTE', 'OPERADOR'),
    [usuario]
  )
  const podeEditar = useMemo(() => temPapel(usuario?.permissoes, 'ADMIN', 'GERENTE'), [usuario])

  const [parceiros, setParceiros] = useState<Parceiro[] | null>(null)
  const [resumo, setResumo] = useState<ParceiroResumo | null>(null)
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const [formAberto, setFormAberto] = useState(false)
  const [editando, setEditando] = useState<Parceiro | null>(null)
  const [detalhe, setDetalhe] = useState<Parceiro | null>(null)
  const [politica, setPolitica] = useState<{ parceiro: Parceiro; produtoId: string } | null>(null)
  const [mudandoStatus, setMudandoStatus] = useState(false)

  const carregar = useCallback(async () => {
    if (!podeVer) return
    setCarregando(true)
    setErro(null)
    try {
      const [lista, kpis, catalogo] = await Promise.all([
        parceirosService.listar(false),
        parceirosService.resumo(),
        produtoService.listar(0, 200),
      ])
      setParceiros(lista)
      setResumo(kpis)
      setProdutos(catalogo?.content ?? [])
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao listar parceiros')
      setParceiros(null)
      setResumo(null)
    } finally {
      setCarregando(false)
    }
  }, [podeVer])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const nomeDoProduto = useCallback(
    (id: string) => produtos.find((p) => p.id === id)?.nome ?? id,
    [produtos]
  )

  const salvar = async (request: ParceiroRequest) => {
    if (editando) {
      const atualizado = await parceirosService.atualizar(editando.id, request)
      setDetalhe((d) => (d && d.id === atualizado.id ? atualizado : d))
    } else {
      await parceirosService.criar(request)
    }
    await carregar()
  }

  const alternarStatus = async (p: Parceiro) => {
    setMudandoStatus(true)
    try {
      const atualizado = await parceirosService.alterarStatus(p.id, !p.ativo)
      setDetalhe(atualizado)
      await carregar()
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível alterar o status')
    } finally {
      setMudandoStatus(false)
    }
  }

  const colunas: ColunaTabelaAcao<Parceiro>[] = [
    {
      titulo: 'Parceiro',
      render: (p) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {p.nome}
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
            {p.email}
          </Typography>
        </Box>
      ),
    },
    {
      titulo: 'Produtos',
      render: (p) =>
        p.produtosOferecidos?.length ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {p.produtosOferecidos.map((id) => (
              <Chip key={id} size="small" label={nomeDoProduto(id)} />
            ))}
          </Stack>
        ) : (
          <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
            Nenhum produto
          </Typography>
        ),
    },
    {
      titulo: 'Status',
      render: (p) => (
        <Chip
          size="small"
          label={p.ativo ? 'Ativo' : 'Inativo'}
          sx={{
            color: p.ativo ? tokens.statusSuccess : tokens.textSecondary,
            borderColor: p.ativo ? tokens.statusSuccess : tokens.divider,
          }}
          variant="outlined"
        />
      ),
    },
    { titulo: 'Modelo', render: (p) => TIPOS_PARCEIRO[p.tipo] ?? p.tipo },
  ]

  const cabecalho = (
    <PageHeader
      title="Parceiros"
      eyebrow="Administração"
      subtitle="Parceiros financeiros e as políticas por produto que cada um opera"
      acao={
        podeEditar ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditando(null)
              setFormAberto(true)
            }}
          >
            Novo parceiro
          </Button>
        ) : undefined
      }
    />
  )

  if (usuario && !podeVer) {
    return (
      <Box>
        {cabecalho}
        <EstadoDeLista
          estado="semPermissao"
          detalhe="O cadastro de parceiros é do back-office. Consultores veem parceiros dentro do produto e da oportunidade."
        />
      </Box>
    )
  }

  const lista = parceiros ?? []

  return (
    <Box>
      {cabecalho}

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            variante="escura"
            label="Cadastrados"
            valor={resumo ? resumo.cadastrados : '—'}
            legenda={resumo ? `${resumo.ativos} ativos` : 'Parceiros na base'}
            carregando={carregando}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Políticas"
            valor={resumo ? resumo.politicas : '—'}
            legenda={
              resumo
                ? `${resumo.politicasAtivas} ativas · ${resumo.parceirosComPolitica} parceiros com política`
                : 'Políticas por produto'
            }
            carregando={carregando}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Seleções"
            valor="—"
            legenda="A escolha do parceiro vive na reserva; sem projeção aqui"
            carregando={carregando}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Matching automático"
            valor="Futuro"
            legenda="Hoje a compatibilidade é conferida por operação, na oportunidade"
            carregando={carregando}
          />
        </Grid>
      </Grid>

      {carregando && !parceiros ? (
        <EstadoDeLista estado="carregando" />
      ) : erro && !parceiros ? (
        <EstadoDeLista estado="erro" detalhe={erro} acao={{ label: 'Tentar de novo', onClick: () => void carregar() }} />
      ) : lista.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhum parceiro cadastrado"
          detalhe={podeEditar ? 'Comece pelo botão "Novo parceiro".' : undefined}
        />
      ) : (
        <TabelaAcao<Parceiro>
          colunas={colunas}
          linhas={lista}
          onAbrir={(p) => setDetalhe(p)}
          chaveDaLinha={(p) => p.id}
        />
      )}

      <ParceiroFormDialog
        aberto={formAberto}
        parceiro={editando}
        produtos={produtos}
        onFechar={() => setFormAberto(false)}
        onSalvar={salvar}
      />

      <Dialog open={!!detalhe} onClose={() => setDetalhe(null)} maxWidth="sm" fullWidth>
        {detalhe && (
          <>
            <DialogTitle>
              {detalhe.nome}
              <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                {TIPOS_PARCEIRO[detalhe.tipo] ?? detalhe.tipo} · CNPJ {detalhe.cnpj} ·{' '}
                {detalhe.ativo ? 'Ativo' : 'Inativo'}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {detalhe.email}
                {detalhe.telefone ? ` · ${detalhe.telefone}` : ''}
              </Typography>
              {detalhe.contato && (
                <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                  Contato: {detalhe.contato}
                </Typography>
              )}

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                Políticas por produto
              </Typography>
              {detalhe.produtosOferecidos?.length ? (
                <List dense disablePadding>
                  {detalhe.produtosOferecidos.map((produtoId) => (
                    <ListItem
                      key={produtoId}
                      disableGutters
                      secondaryAction={
                        <Button
                          size="small"
                          onClick={() => setPolitica({ parceiro: detalhe, produtoId })}
                        >
                          {podeEditar ? 'Editar política' : 'Ver política'}
                        </Button>
                      }
                    >
                      <ListItemText primary={nomeDoProduto(produtoId)} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                  Este parceiro ainda não opera nenhum produto — edite o cadastro para vincular.
                </Typography>
              )}

              <Typography variant="caption" sx={{ color: tokens.textSecondary, display: 'block', mt: 2 }}>
                Matching automático entre políticas e operações: previsto para uma onda futura.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetalhe(null)}>Fechar</Button>
              {podeEditar && (
                <>
                  <Button
                    color={detalhe.ativo ? 'warning' : 'success'}
                    disabled={mudandoStatus}
                    onClick={() => void alternarStatus(detalhe)}
                  >
                    {detalhe.ativo ? 'Desativar' : 'Ativar'}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setEditando(detalhe)
                      setFormAberto(true)
                    }}
                  >
                    Editar
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {politica && (
        <PoliticaParceiroDialog
          aberto
          parceiroId={politica.parceiro.id}
          parceiroNome={politica.parceiro.nome}
          produtoId={politica.produtoId}
          podeEditar={podeEditar}
          onFechar={() => setPolitica(null)}
        />
      )}
    </Box>
  )
}

export default ParceirosPage
